import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { brandingService, BrandingConfig, defaultBranding } from '@/services/branding.service';

interface ThemeContextValue {
  branding: BrandingConfig;
  isLoading: boolean;
  refetch: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

// Helper function to convert hex to RGB
function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
  }
  return '0, 0, 0';
}

// Apply CSS variables to document
function applyTheme(branding: BrandingConfig) {
  const root = document.documentElement;

  // Primary colors (brand-gold)
  root.style.setProperty('--color-primary', branding.primaryColor);
  root.style.setProperty('--color-primary-rgb', hexToRgb(branding.primaryColor));

  // Secondary colors (brand-cigar)
  root.style.setProperty('--color-secondary', branding.secondaryColor);
  root.style.setProperty('--color-secondary-rgb', hexToRgb(branding.secondaryColor));

  // Accent/ivory colors
  root.style.setProperty('--color-accent', branding.accentColor);
  root.style.setProperty('--color-accent-rgb', hexToRgb(branding.accentColor));

  // Background colors (brand-ink)
  root.style.setProperty('--color-background', branding.backgroundColor);
  root.style.setProperty('--color-background-rgb', hexToRgb(branding.backgroundColor));

  // Surface colors (brand-smoke)
  root.style.setProperty('--color-surface', branding.surfaceColor);
  root.style.setProperty('--color-surface-rgb', hexToRgb(branding.surfaceColor));

  // Text colors
  root.style.setProperty('--color-text', branding.textColor);
  root.style.setProperty('--color-text-rgb', hexToRgb(branding.textColor));

  // Update meta theme color
  const metaTheme = document.querySelector('meta[name="theme-color"]');
  if (metaTheme) {
    metaTheme.setAttribute('content', branding.backgroundColor);
  }

  // Update favicon if provided
  if (branding.faviconUrl) {
    const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    if (favicon) {
      favicon.href = branding.faviconUrl;
    }
  }

  // Update document title
  if (branding.appName) {
    document.title = branding.appName;
  }
}

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [initialLoad, setInitialLoad] = useState(true);

  const { data: branding, isLoading, refetch } = useQuery({
    queryKey: ['branding'],
    queryFn: () => brandingService.getPublic(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Apply default theme immediately on mount
  useEffect(() => {
    if (initialLoad) {
      applyTheme(defaultBranding);
      setInitialLoad(false);
    }
  }, [initialLoad]);

  // Apply fetched theme when available
  useEffect(() => {
    if (branding) {
      applyTheme(branding);
    }
  }, [branding]);

  const value: ThemeContextValue = {
    branding: branding || defaultBranding,
    isLoading,
    refetch,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
